import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BASE_URL } from '../Utils/Function';
import { FaPlus, FaEdit, FaTrash, FaCloudUploadAlt, FaTimes } from 'react-icons/fa';

const AddServices = () => {
    const [services, setServices] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState({ show: false, id: null, name: '' });
    const [editData, setEditData] = useState(null);
    const [formData, setFormData] = useState({ serv_name: '', image: null });
    const [previewUrl, setPreviewUrl] = useState('');
    const [message, setMessage] = useState({ type: '', text: '' });
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        fetchServices();
    }, []);

    const fetchServices = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${BASE_URL}/service/getAllService.php`);
            if (response.data.status === 200) {
                setServices(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching services:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({ ...formData, image: file });
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const resetForm = () => {
        setFormData({ serv_name: '', image: null });
        setPreviewUrl('');
        setEditData(null);
        setMessage({ type: '', text: '' });
    };

    const handleOpenModal = (service = null) => {
        resetForm();
        if (service) {
            setEditData(service);
            setFormData({ serv_name: service.serv_name, image: null });
            setPreviewUrl(service.image_url);
        }
        setShowModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setMessage({ type: '', text: '' });

        const data = new FormData();
        if (editData) data.append('serv_id', editData.serv_id);
        data.append('serv_name', formData.serv_name);
        if (formData.image) data.append('image', formData.image);

        try {
            const endpoint = editData
                ? `${BASE_URL}/service/updateService.php`
                : `${BASE_URL}/service/createService.php`;

            const response = await axios.post(endpoint, data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (response.data.status === 200) {
                setMessage({ type: 'success', text: response.data.message });
                fetchServices();
                setTimeout(() => setShowModal(false), 1500);
            } else {
                setMessage({ type: 'error', text: response.data.message });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'Operation failed. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = (service) => {
        setDeleteModal({
            show: true,
            id: service.serv_id,
            name: service.serv_name
        });
    };

    const confirmDelete = async () => {
        if (!deleteModal.id) return;
        setIsDeleting(true);

        try {
            const response = await axios.delete(`${BASE_URL}/service/deleteService.php`, {
                data: { serv_id: deleteModal.id }
            });
            if (response.data.status === 200) {
                fetchServices();
                setDeleteModal({ show: false, id: null, name: '' });
            }
        } catch (error) {
            console.error('Error deleting service:', error);
            alert('Failed to delete service. Check backend connection.');
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="admin-services-page">
            <div className="d-flex justify-content-between align-items-end mb-5">
                <div>
                    <span className="section-index">CATALOGUE</span>
                    <h2 className="display-6 text-white text-uppercase m-0" style={{ letterSpacing: '3px' }}>Service Management</h2>
                </div>
                <button className="btn-admin-add" onClick={() => handleOpenModal()}>
                    <FaPlus className="me-2" /> ADD NEW SERVICE
                </button>
            </div>

            {isLoading ? (
                <div className="text-center py-5">
                    <div className="spinner-border text-secondary-color" role="status"></div>
                </div>
            ) : (
                <div className="services-grid">
                    {services.map((service) => (
                        <div key={service.serv_id} className="admin-service-card">
                            <div className="service-card-image">
                                <img
                                    src={service.image_url.startsWith('http') ? service.image_url : `${BASE_URL}/${service.image_url}`}
                                    alt={service.serv_name}
                                />
                                <div className="service-card-overlay">
                                    <button className="card-action-btn edit" title="Edit Service" onClick={() => handleOpenModal(service)}>
                                        <FaEdit />
                                    </button>
                                    <button className="card-action-btn delete" title="Delete Service" onClick={() => handleDelete(service)}>
                                        <FaTrash />
                                    </button>
                                </div>
                            </div>
                            <div className="service-card-info">
                                <h3 className="service-title">{service.serv_name}</h3>
                            </div>
                        </div>
                    ))}

                    {services.length === 0 && (
                        <div className="empty-state">
                            <p className="text-white-50">No services found in the catalogue. Start by adding a new one.</p>
                        </div>
                    )}
                </div>
            )}

            {/* Service Modal */}
            {showModal && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content">
                        <div className="modal-header-custom">
                            <h4 className="m-0 text-white">{editData ? 'EDIT SERVICE' : 'ADD NEW SERVICE'}</h4>
                            <button className="btn-close-modal" onClick={() => setShowModal(false)}><FaTimes /></button>
                        </div>

                        <form onSubmit={handleSubmit} className="modal-body-custom">
                            <div className="mb-4">
                                <label className="admin-label mb-2">SERVICE NAME</label>
                                <div className="auth-input-wrapper">
                                    <input
                                        type="text"
                                        className="auth-input"
                                        placeholder="e.g. Interior Design"
                                        value={formData.serv_name}
                                        onChange={(e) => setFormData({ ...formData, serv_name: e.target.value })}
                                        required
                                    />
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="admin-label mb-2">SERVICE VISUAL</label>
                                <div
                                    className="upload-dropzone"
                                    onClick={() => document.getElementById('imageInput').click()}
                                >
                                    {previewUrl ? (
                                        <div className="preview-container">
                                            <img
                                                src={previewUrl.startsWith('blob:') || previewUrl.startsWith('http') ? previewUrl : `${BASE_URL}/${previewUrl}`}
                                                alt="Preview"
                                            />
                                            <div className="upload-overlay"><FaCloudUploadAlt /> CHANGE IMAGE</div>
                                        </div>
                                    ) : (
                                        <div className="upload-placeholder">
                                            <FaCloudUploadAlt className="mb-3" style={{ fontSize: '2.5rem' }} />
                                            <p className="m-0">CLICK TO UPLOAD SERVICE IMAGE</p>
                                            <small className="text-white-50">JPG, PNG, GIF (MAX 5MB)</small>
                                        </div>
                                    )}
                                    <input
                                        type="file"
                                        id="imageInput"
                                        hidden
                                        onChange={handleFileChange}
                                        accept="image/*"
                                        required={!editData}
                                    />
                                </div>
                            </div>

                            {message.text && (
                                <div className={`admin-msg mb-4 ${message.type}`}>
                                    {message.text}
                                </div>
                            )}

                            <div className="pt-3">
                                <button type="submit" className="btn-admin-submit w-100 py-3" disabled={isSubmitting}>
                                    {isSubmitting ? (
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                    ) : (
                                        <FaPlus className="me-2" />
                                    )}
                                    {editData ? 'SAVE CHANGES' : 'CREATE SERVICE'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Custom Delete Confirmation Modal */}
            {deleteModal.show && (
                <div className="admin-modal-overlay">
                    <div className="admin-modal-content confirm-modal">
                        <div className="modal-header-custom border-0 pb-0">
                            <h4 className="m-0 text-white">AUTHORIZATION REQUIRED</h4>
                            <button className="btn-close-modal" onClick={() => setDeleteModal({ show: false, id: null, name: '' })}><FaTimes /></button>
                        </div>
                        <div className="modal-body-custom pt-4 text-center">
                            <div className="mb-4">
                                <div className="confirm-icon-wrap delete">
                                    <FaTrash />
                                </div>
                            </div>
                            <h5 className="text-white mb-3">Confirm Permanent Deletion</h5>
                            <p className="text-white-50 small mb-4">
                                Are you sure you want to delete the service <strong className="text-white">"{deleteModal.name}"</strong>?
                                This action is irreversible and will remove all associated catalog data.
                            </p>

                            <div className="d-flex gap-3 mt-5">
                                <button className="btn-admin-submit btn-delete-final flex-grow-1" onClick={confirmDelete} disabled={isDeleting}>
                                    {isDeleting ? 'DELETING...' : 'CONFIRM DELETION'}
                                </button>
                                <button className="btn-admin-outline px-4" onClick={() => setDeleteModal({ show: false, id: null, name: '' })} disabled={isDeleting}>
                                    CANCEL
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .btn-admin-add {
                    background: var(--secondary-color);
                    border: none;
                    color: black;
                    padding: 12px 24px;
                    font-size: 0.75rem;
                    font-weight: 600;
                    letter-spacing: 2px;
                    border-radius: 2px;
                    transition: all 0.3s ease;
                }

                .btn-admin-add:hover {
                    background: #d4ae6b;
                    transform: translateY(-3px);
                    box-shadow: 0 5px 15px rgba(197, 160, 89, 0.2);
                }

                .services-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 30px;
                    padding-bottom: 50px;
                }

                .admin-service-card {
                    background: rgba(255, 255, 255, 0.03);
                    border: 1px solid rgba(255, 255, 255, 0.05);
                    border-radius: 4px;
                    overflow: hidden;
                    transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
                    position: relative;
                }

                .admin-service-card:hover {
                    transform: translateY(-10px);
                    border-color: rgba(197, 160, 89, 0.3);
                    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
                }

                .service-card-image {
                    width: 100%;
                    height: 220px;
                    position: relative;
                    background: rgba(255,255,255,0.01);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                    border-bottom: 1px solid rgba(255,255,255,0.05);
                }

                .service-card-image img {
                    max-width: 100%;
                    max-height: 100%;
                    object-fit: contain;
                    filter: drop-shadow(0 10px 20px rgba(0,0,0,0.3));
                    transition: transform 0.6s cubic-bezier(0.165, 0.84, 0.44, 1);
                }

                .admin-service-card:hover .service-card-image img {
                    transform: scale(1.1);
                }

                .service-card-overlay {
                    position: absolute;
                    inset: 0;
                    background: rgba(0,0,0,0.6);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 15px;
                    opacity: 0;
                    transition: opacity 0.3s ease;
                    backdrop-filter: blur(5px);
                }

                .admin-service-card:hover .service-card-overlay {
                    opacity: 1;
                }

                .card-action-btn {
                    width: 45px;
                    height: 45px;
                    border-radius: 50%;
                    border: 1px solid rgba(255,255,255,0.2);
                    background: rgba(255,255,255,0.1);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.1rem;
                    transition: all 0.3s ease;
                }

                .card-action-btn.edit:hover { background: var(--secondary-color); color: black; border-color: var(--secondary-color); }
                .card-action-btn.delete:hover { background: #dc3545; color: white; border-color: #dc3545; }

                .service-card-info {
                    padding: 20px;
                }

                .service-id-badge {
                    font-size: 0.6rem;
                    color: var(--secondary-color);
                    letter-spacing: 2px;
                    background: rgba(197, 160, 89, 0.1);
                    padding: 4px 10px;
                    border-radius: 20px;
                    margin-bottom: 10px;
                    display: inline-block;
                }

                .service-title {
                    color: white;
                    font-size: 1.1rem;
                    letter-spacing: 1px;
                    margin: 0;
                    font-weight: 400;
                    text-transform: uppercase;
                }

                /* Modal Styling */
                .admin-modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.8);
                    backdrop-filter: blur(10px);
                    z-index: 10000;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 20px;
                }

                .admin-modal-content {
                    width: 100%;
                    max-width: 500px;
                    background: #0a0a0a;
                    border: 1px solid rgba(255,255,255,0.1);
                    border-radius: 4px;
                    overflow: hidden;
                    box-shadow: 0 50px 100px rgba(0,0,0,0.5);
                }

                .modal-header-custom {
                    padding: 25px 30px;
                    border-bottom: 1px solid rgba(255,255,255,0.05);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .modal-header-custom h4 {
                    font-size: 0.9rem;
                    letter-spacing: 3px;
                    font-weight: 400;
                }

                .btn-close-modal {
                    background: none;
                    border: none;
                    color: rgba(255,255,255,0.4);
                    font-size: 1.2rem;
                    transition: color 0.3s ease;
                }

                .btn-close-modal:hover { color: #fff; }

                .modal-body-custom {
                    padding: 40px;
                }

                .upload-dropzone {
                    width: 100%;
                    height: 180px;
                    border: 1px dashed rgba(197, 160, 89, 0.3);
                    background: rgba(255,255,255,0.02);
                    border-radius: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    overflow: hidden;
                    position: relative;
                    transition: all 0.3s ease;
                }

                .upload-dropzone:hover { border-color: var(--secondary-color); background: rgba(197, 160, 89, 0.03); }

                .upload-placeholder {
                    text-align: center;
                    color: var(--secondary-color);
                    font-size: 0.7rem;
                    letter-spacing: 2px;
                }

                .preview-container {
                    width: 100%;
                    height: 100%;
                    position: relative;
                }

                .preview-container img {
                    width: 100%;
                    height: 100%;
                    object-fit: contain;
                    padding: 20px;
                }

                .upload-overlay {
                    position: absolute;
                    inset: 0;
                    background: rgba(0,0,0,0.6);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    color: #fff;
                    font-size: 0.7rem;
                    letter-spacing: 2px;
                    opacity: 0;
                    transition: opacity 0.3s ease;
                    gap: 10px;
                }

                .preview-container:hover .upload-overlay { opacity: 1; }

                .admin-msg {
                    padding: 12px 15px;
                    border-radius: 4px;
                    font-size: 0.8rem;
                    letter-spacing: 1px;
                }

                .admin-msg.success { background: rgba(40, 167, 69, 0.1); border-left: 3px solid #28a745; color: #28a745; }
                .admin-msg.error { background: rgba(220, 53, 69, 0.1); border-left: 3px solid #dc3545; color: #dc3545; }

                .empty-state {
                    grid-column: 1 / -1;
                    text-align: center;
                    padding: 80px 0;
                    background: rgba(255,255,255,0.02);
                    border: 1px dashed rgba(255,255,255,0.05);
                    border-radius: 4px;
                }

                /* Delete Modal Specific Styles */
                .confirm-modal {
                    max-width: 450px;
                }

                .confirm-icon-wrap {
                    width: 70px;
                    height: 70px;
                    background: rgba(255, 193, 7, 0.1);
                    color: #ffc107;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto;
                    font-size: 2rem;
                    border: 1px solid rgba(255, 193, 7, 0.2);
                }

                .confirm-icon-wrap.delete {
                    background: rgba(220, 53, 69, 0.1);
                    color: #dc3545;
                    border-color: rgba(220, 53, 69, 0.3);
                    box-shadow: 0 0 30px rgba(220, 53, 69, 0.1);
                }

                .btn-admin-submit.btn-delete-final {
                    background: #dc3545;
                    color: white;
                    border: 1px solid #dc3545;
                    box-shadow: 0 5px 15px rgba(220, 53, 69, 0.2);
                }

                .btn-admin-submit.btn-delete-final:hover:not(:disabled) {
                    background: #bb2d3b;
                    border-color: #b02a37;
                    transform: translateY(-3px);
                }

                .btn-admin-outline {
                    background: transparent;
                    border: 1px solid rgba(255,255,255,0.1);
                    color: white;
                    border-radius: 2px;
                    font-size: 0.75rem;
                    letter-spacing: 2px;
                    transition: all 0.3s ease;
                }

                .btn-admin-outline:hover:not(:disabled) {
                    background: rgba(255,255,255,0.05);
                    border-color: rgba(255,255,255,0.2);
                }
            `}</style>
        </div>
    );
};

export default AddServices;
